import { z } from "zod";

export type ContractFieldType = "text" | "phone" | "country" | string;

export interface ContractField {
  key: string;
  label: string;
  fieldType: ContractFieldType;
  required: boolean;
  width: string;
  placeholder: string;
  helpText: string;
  section: string;
  sectionTitle: string;
}

const MAX_VALUE_LENGTH = 500;

// Mirrors the server-side check in contracts/services.py. A leading digit,
// plus, bracket or slash followed by at least four more phone characters.
const PHONE_RE = /^[+()/\d][\d\s()/.+-]{4,}$/;

function validatorFor(
  field: ContractField,
  countries: readonly string[],
): z.ZodTypeAny {
  if (field.fieldType === "country") {
    // An empty selection must read as "required", not as "unknown country",
    // so the empty case is handled by the required branch below.
    return z
      .string()
      .refine((value) => value === "" || countries.includes(value), {
        message: "Bitte wählen Sie ein Land aus der Liste aus.",
      });
  }

  if (field.fieldType === "phone") {
    return z
      .string()
      .max(MAX_VALUE_LENGTH, `Darf höchstens ${MAX_VALUE_LENGTH} Zeichen lang sein.`)
      .refine((value) => value === "" || PHONE_RE.test(value), {
        message: "Bitte geben Sie eine gültige Telefonnummer an.",
      });
  }

  return z
    .string()
    .max(MAX_VALUE_LENGTH, `Darf höchstens ${MAX_VALUE_LENGTH} Zeichen lang sein.`);
}

export function buildContractSchema(
  fields: ContractField[],
  countries: readonly string[] = [],
): z.ZodTypeAny {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of fields) {
    let validator = validatorFor(field, countries);
    if (field.required) {
      validator = validator.refine((value) => String(value).trim() !== "", {
        message: "Dieses Feld ist erforderlich.",
      });
    }
    shape[field.key] = validator;
  }
  return z.object(shape);
}
